import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";
import { apiProbationPolicies } from "@/api/probation-policiy.api.ts";
import { ApiProbationPolicyPayload } from "@/types/probation-policy.types.ts";
import { useMemo, useState } from "react";
import { SelectItem } from "@skailer-com/uikit";
import { escapeRegExp } from "lodash";

const probationPoliciesKeys = createQueryKeys("probationPolicies", {
  all: null,
});

export const useProbationPolicies = (enabled: boolean = true) => {
  return useQuery({
    ...probationPoliciesKeys.all,
    staleTime: 60 * 1000,
    enabled: enabled,
    queryFn: async () => {
      const result = await apiProbationPolicies.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useProbationPolicySave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: { id?: number; payload: ApiProbationPolicyPayload }) => {
      const result = id
        ? await apiProbationPolicies.update(id, payload)
        : await apiProbationPolicies.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: probationPoliciesKeys.all.queryKey });
    },

    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useProbationPolicyDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiProbationPolicies.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: probationPoliciesKeys.all.queryKey });
    },

    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useProbationPoliciesSearchInline = (enabled: boolean = true) => {
  const [searchKey, setSearchKey] = useState<string | undefined>(undefined);

  const { data, isLoading } = useProbationPolicies(enabled);

  const options = useMemo(() => {
    const allData = (data || []) as SelectItem[];
    const regex = new RegExp(escapeRegExp(searchKey), "i");

    return allData.filter((option) => regex.test(option.title));
  }, [data, searchKey]);

  function search(text: string | undefined) {
    setSearchKey(text);
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};
